// src/components/dashboard/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { formatOrgDate } from '../../utils/dateUtils';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { reportsService } from '../../services/reportsService';
import { staffService } from '../../services/staffService';
import { attendanceService } from '../../services/attendanceService';
import { leavesService } from '../../services/leavesService';
import { licenseService } from '../../services/licenseService';
import { premisesService } from '../../services/premisesService';
import LoadingSpinner from '../common/LoadingSpinner';
import { usePageTitle } from '../../hooks/usePageTitle';

const AdminDashboard = () => {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremisesAdmin = user?.user_type === 'app_admin';

  const [premisesName, setPremisesName] = useState(user?.premises_name || 'Unassigned Premises');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  const [stats, setStats] = useState({
    totalStaff: 0,
    totalAllowedStaff: 0,
    activeStaff: 0,
    inactiveStaff: 0,
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
    yesterdayLate: 0,
    onLeaveToday: 0,
    presentPercentage: 0,
    avgAttendance: 0,
    pendingLeaves: 0,
    totalPremises: 0,
    monthlyHours: 0,
  });

  const [alerts, setAlerts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    let isMounted = true;
    if (!isPremisesAdmin || user?.premises_name) return;
    if (!user?.premises_id) { setPremisesName('Unassigned Premises'); return; }

    premisesService.getPremisesById(user.premises_id)
      .then(r => { if (isMounted) setPremisesName(r?.premises_name || 'Unassigned Premises'); })
      .catch(() => { if (isMounted) setPremisesName('Unassigned Premises'); });

    return () => { isMounted = false; };
  }, [isPremisesAdmin, user?.premises_id, user?.premises_name]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(d.getDate() - (6 - i));
        return d.toISOString().split('T')[0];
      });

      const [
        dashboardStats,
        staffStats,
        todayAtt,
        yesterdayAtt,
        leaveStats,
        weeklyAtt,
        licenseStatus,
      ] = await Promise.all([
        reportsService.getDashboardStats().catch(() => ({})),
        staffService.getStaffStats().catch(() => ({ total: 0, active: 0 })),
        attendanceService.getAttendanceStats({ date_from: todayStr, date_to: todayStr }).catch(() => ({})),
        attendanceService.getAttendanceStats({ date_from: yesterdayStr, date_to: yesterdayStr }).catch(() => ({ total_late: 0 })),
        leavesService.getLeaveStats().catch(() => ({ pending: 0, approved: 0, rejected: 0 })),
        Promise.all(last7Days.map(date =>
          attendanceService.getAttendanceStats({ date_from: date, date_to: date }).catch(() => ({ total_present: 0, total_late: 0 }))
        )),
        // null (not a fake zero-filled object) on failure: a transient failure
        // here must not be indistinguishable from "this org genuinely has 0
        // staff and 0 allowed" - that's what was producing the misleading
        // "0/∞" that flips to the real numbers on a retry/refresh.
        licenseService.getLicenseStatus().catch(() => null),
      ]);
      setSubscriptionInfo(licenseStatus || null);

      // Falls through to the independently-fetched staffStats total when the
      // license-status call fails but the staff-stats call still succeeded,
      // instead of forcing a wrong "0".
      const totalStaff = Number(licenseStatus?.current_staff_count ?? staffStats?.total ?? 0);
      // null (not 0) when the license-status call failed, so the UI can show
      // "unknown" instead of a misleading "∞" (which means something entirely
      // different: a confirmed, genuinely unlimited/zero-cap allowance).
      const totalAllowed = licenseStatus?.allowed_staff_count != null
        ? Number(licenseStatus.allowed_staff_count)
        : null;
      const activeStaff = Number(staffStats?.active ?? 0);
      const inactiveStaff = Number(staffStats?.inactive ?? 0);
      const presentToday = Number(todayAtt?.total_present ?? 0);
      const absentToday = Number(todayAtt?.total_absent ?? 0);
      const lateToday = Number(todayAtt?.total_late ?? 0);
      const onLeaveToday = Number(todayAtt?.total_on_leave ?? 0);
      const yesterdayLate = Number(yesterdayAtt?.total_late ?? 0);
      const avgAttendance = Number(dashboardStats?.avg_attendance ?? 0);
      const pendingLeaves = Number(leaveStats?.pending ?? 0);
      const activePremises = Number(dashboardStats?.active_premises ?? 0);
      const monthlyHours = Number(dashboardStats?.monthly_hours ?? 0);
      const presentPct = totalStaff > 0 ? ((presentToday / totalStaff) * 100).toFixed(1) : 0;

      setStats({
        totalStaff, totalAllowedStaff: totalAllowed, activeStaff, inactiveStaff,
        presentToday, absentToday, lateToday, yesterdayLate, onLeaveToday,
        presentPercentage: presentPct, avgAttendance, pendingLeaves,
        totalPremises: activePremises, monthlyHours,
      });

      // Alerts
      const newAlerts = [];
      if (pendingLeaves > 0) {
        newAlerts.push({
          id: 'pending_leaves', type: 'warning', icon: 'fas fa-calendar-times',
          title: 'Pending Leave Requests',
          message: `${pendingLeaves} leave request${pendingLeaves !== 1 ? 's' : ''} awaiting your approval.`,
          action: 'Review Leaves →', link: '/leaves'
        });
      }
      if (lateToday > 0 && totalStaff > 0 && (lateToday / totalStaff) > 0.15) {
        newAlerts.push({
          id: 'high_late', type: 'warning', icon: 'fas fa-clock',
          title: 'High Late Arrivals Today',
          message: `${lateToday} staff member${lateToday !== 1 ? 's' : ''} arrived late today (${Math.round((lateToday / totalStaff) * 100)}% of workforce).`,
          action: 'View Attendance →', link: '/attendance'
        });
      }
      if (avgAttendance > 0 && avgAttendance < 70) {
        newAlerts.push({
          id: 'low_attendance', type: 'critical', icon: 'fas fa-user-clock',
          title: 'Low Monthly Attendance',
          message: `Attendance this month is ${avgAttendance.toFixed(1)}% — below the 70% threshold.`,
          action: 'View Reports →', link: '/reports'
        });
      }
      if (totalAllowed > 0 && totalStaff >= totalAllowed) {
        newAlerts.push({
          id: 'license_limit', type: 'info', icon: 'fas fa-id-card',
          title: 'Staff Limit Reached',
          message: `You are using ${totalStaff} of ${totalAllowed} allowed staff slots. Upgrade your plan to add more.`,
          action: 'Manage Subscription →', link: '/subscriptions/manage'
        });
      }
      setAlerts(newAlerts);

      // Weekly trend
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      setAttendanceTrend(weeklyAtt.map((dayStats, i) => {
        const d = new Date(last7Days[i]);
        const pct = totalStaff > 0 ? Math.round((Number(dayStats?.total_present ?? 0) / totalStaff) * 100) : 0;
        return { day: dayNames[d.getDay()], date: last7Days[i], percentage: Math.min(pct, 100), late: Number(dayStats?.total_late ?? 0) };
      }));

      // Recent activity
      const activities = [];
      if (lateToday > 0) activities.push({ id: 1, icon: 'fas fa-exclamation-triangle', color: 'warning', message: `${lateToday} staff member${lateToday !== 1 ? 's' : ''} marked late today`, time: 'Today' });
      if (pendingLeaves > 0) activities.push({ id: 2, icon: 'fas fa-calendar-times', color: 'warning', message: `${pendingLeaves} leave request${pendingLeaves !== 1 ? 's' : ''} pending approval`, time: 'Pending' });
      activities.push({ id: 3, icon: 'fas fa-user-check', color: 'success', message: `${presentToday} of ${totalStaff} staff present today (${presentPct}%)`, time: 'Today' });
      if (absentToday > 0) activities.push({ id: 4, icon: 'fas fa-user-slash', color: 'error', message: `${absentToday} staff member${absentToday !== 1 ? 's' : ''} absent today`, time: 'Today' });
      if (avgAttendance > 0) activities.push({ id: 5, icon: 'fas fa-chart-line', color: avgAttendance >= 85 ? 'success' : 'info', message: `Monthly attendance rate: ${avgAttendance.toFixed(1)}%`, time: 'This Month' });
      setRecentActivity(activities);

      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  const dismissAlert = (id) => setDismissedAlerts(prev => new Set([...prev, id]));
  const visibleAlerts = alerts.filter(a => !dismissedAlerts.has(a.id));

  const quickActions = [
    { title: 'Add Staff Member', icon: 'fas fa-user-plus', link: '/staff', color: 'primary', description: 'Add new staff to your organisation' },
    { title: 'View Attendance', icon: 'fas fa-clock', link: '/attendance', color: 'success', description: 'View and manage staff attendance' },
    { title: 'Manage Leaves', icon: 'fas fa-calendar-alt', link: '/leaves', color: 'warning', description: 'Review and approve leave requests' },
    { title: 'Generate Report', icon: 'fas fa-chart-bar', link: '/reports', color: 'info', description: 'Create attendance and performance reports' },
    { title: 'Manage Premises', icon: 'fas fa-building', link: '/premises', color: 'purple', description: 'Add or edit office locations' },
    { title: 'Manage Shifts', icon: 'fas fa-business-time', link: '/shifts', color: 'secondary', description: 'Configure staff work shifts' },
  ];

  if (loading) return <LoadingSpinner message="Loading dashboard..." />;

  const licenseUsagePct = stats.totalAllowedStaff > 0
    ? Math.min(Math.round((stats.totalStaff / stats.totalAllowedStaff) * 100), 100)
    : 0;

  return (
    <div className="admin-dashboard">
      <div className="container">

        {/* Welcome Header */}
        <div className="welcome-section">
          <div className="welcome-content">
            <h1>Welcome back, {user?.username}!</h1>
            <p>Here's what's happening in your organisation today</p>
            {isPremisesAdmin && (
              <div className="premises-badge">
                <i className="fas fa-building"></i>
                Premises: <strong>{premisesName}</strong>
              </div>
            )}
          </div>
          <div className="date-time">
            <div className="current-date">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
            <div className="current-time">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        </div>

        {/* Error */}
        {loadError && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {loadError}
          </div>
        )}

        {/* Subscription Status */}
        {(() => {
          const endDate = subscriptionInfo?.end_date ? new Date(subscriptionInfo.end_date) : null;
          const today   = new Date(); today.setHours(0, 0, 0, 0);
          const daysLeft = endDate
            ? Math.ceil((endDate.setHours(0,0,0,0) - today) / 86400000)
            : null;

          const isExpired   = subscriptionInfo?.is_expired || daysLeft === 0;
          const isExpiring  = !isExpired && daysLeft !== null && daysLeft <= 15;

          // Only show the card when there is something to warn about
          const showCard = isExpired || !subscriptionInfo?.has_active_subscription || isExpiring;

          if (!showCard) return null;

          const cardClass = isExpired ? 'critical'
            : daysLeft !== null && daysLeft <= 3 ? 'critical'
            : daysLeft !== null && daysLeft <= 7 ? 'warning'
            : 'warning';

          const icon = isExpired ? 'fa-exclamation-circle'
            : daysLeft !== null && daysLeft <= 3 ? 'fa-exclamation-triangle'
            : 'fa-clock';

          const title = isExpired ? 'Subscription Expired'
            : daysLeft === 0 ? 'Subscription Expires Today!'
            : isExpiring ? `Subscription Expiring in ${daysLeft} Day${daysLeft === 1 ? '' : 's'}`
            : 'Subscription Status';

          const message = isExpired
            ? 'Your subscription has expired. Renew now to restore full access.'
            : !subscriptionInfo?.has_active_subscription
            ? 'No active subscription found. Subscribe to continue.'
            : daysLeft !== null && daysLeft <= 3
            ? `URGENT: Your subscription expires on ${formatOrgDate(subscriptionInfo.end_date, subscriptionInfo.timezone)}. Renew immediately to avoid service interruption.`
            : daysLeft !== null && daysLeft <= 7
            ? `Your subscription expires on ${formatOrgDate(subscriptionInfo.end_date, subscriptionInfo.timezone)} (${daysLeft} days). Renew soon to avoid interruption.`
            : `Your subscription expires on ${formatOrgDate(subscriptionInfo.end_date, subscriptionInfo.timezone)} (${daysLeft} days remaining). Consider renewing early.`;

          return (
            <div className="alerts-section">
              <div
                className={`alert-card ${cardClass}`}
                onClick={() => navigate('/subscriptions/purchase')}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && navigate('/subscriptions/purchase')}
              >
                <div className="alert-header">
                  <div className="alert-title-row">
                    <i className={`fas ${icon} alert-icon-sm`}></i>
                    <span className="alert-title">{title}</span>
                  </div>
                </div>
                <div className="alert-message">{message}</div>
                <div className="alert-action renew-action">
                  {isExpired ? 'Renew Now →' : 'Renew Early →'}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Alerts */}
        {visibleAlerts.length > 0 && (
          <div className="alerts-section">
            <div className="alerts-section-header">
              <h3><i className="fas fa-bell"></i> Alerts</h3>
              <span className="alerts-count">{visibleAlerts.length} active</span>
            </div>
            <div className="alerts-grid">
              {visibleAlerts.map(alert => (
                <div
                  key={alert.id}
                  className={`alert-card ${alert.type}`}
                  onClick={() => navigate(alert.link)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && navigate(alert.link)}
                >
                  <div className="alert-header">
                    <div className="alert-title-row">
                      <i className={`${alert.icon} alert-icon-sm`}></i>
                      <span className="alert-title">{alert.title}</span>
                    </div>
                    <button className="alert-dismiss" onClick={e => { e.stopPropagation(); dismissAlert(alert.id); }} title="Dismiss">
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="alert-message">{alert.message}</div>
                  <div className="alert-action">{alert.action}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="stats-grid">

          {/* Staff */}
          <div className="stat-card clickable" onClick={() => navigate('/staff')}>
            <div className="stat-icon-wrap primary"><i className="fas fa-users"></i></div>
            <div className="stat-content">
              <div className="stat-number">{stats.totalStaff}<span className="stat-of">/{
                stats.totalAllowedStaff > 0 ? stats.totalAllowedStaff
                  : stats.totalAllowedStaff === null ? '—'
                    : '∞'
              }</span></div>
              <div className="stat-label">Total Staff</div>
              <div className="stat-sub-badges">
                <span className="sub-badge active"><i className="fas fa-circle"></i> {stats.activeStaff} Active</span>
                <span className="sub-badge inactive"><i className="fas fa-circle"></i> {stats.inactiveStaff} Inactive</span>
              </div>
              {stats.totalAllowedStaff > 0 && (
                <div className="license-bar">
                  <div className="license-fill" style={{ width: `${licenseUsagePct}%`, background: licenseUsagePct >= 90 ? '#e74c3c' : licenseUsagePct >= 70 ? '#f39c12' : '#27ae60' }}></div>
                </div>
              )}
            </div>
            <i className="fas fa-arrow-right stat-arrow"></i>
          </div>

          {/* Present Today */}
          <div className="stat-card clickable" onClick={() => navigate('/attendance')}>
            <div className="stat-icon-wrap success"><i className="fas fa-user-check"></i></div>
            <div className="stat-content">
              <div className="stat-number">{stats.presentToday}</div>
              <div className="stat-label">Present Today</div>
              <div className="stat-sub-badges">
                <span className="sub-badge present">{stats.presentPercentage}% rate</span>
                {stats.absentToday > 0 && <span className="sub-badge absent"><i className="fas fa-user-slash"></i> {stats.absentToday} Absent</span>}
              </div>
            </div>
            <i className="fas fa-arrow-right stat-arrow"></i>
          </div>

          {/* Late Arrivals */}
          <div className="stat-card clickable" onClick={() => navigate('/attendance')}>
            <div className={`stat-icon-wrap ${stats.lateToday > 0 ? 'warning' : 'success'}`}>
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.lateToday}</div>
              <div className="stat-label">Late Today</div>
              <div className="stat-sub-badges">
                {stats.lateToday < stats.yesterdayLate
                  ? <span className="sub-badge good"><i className="fas fa-arrow-down"></i> {stats.yesterdayLate - stats.lateToday} less than yesterday</span>
                  : stats.lateToday > stats.yesterdayLate
                  ? <span className="sub-badge bad"><i className="fas fa-arrow-up"></i> +{stats.lateToday - stats.yesterdayLate} from yesterday</span>
                  : <span className="sub-badge neutral">Same as yesterday</span>
                }
              </div>
            </div>
            <i className="fas fa-arrow-right stat-arrow"></i>
          </div>

          {/* Pending Leaves */}
          <div className="stat-card clickable" onClick={() => navigate('/leaves')}>
            <div className={`stat-icon-wrap ${stats.pendingLeaves > 0 ? 'warning' : 'info'}`}>
              <i className="fas fa-calendar-times"></i>
            </div>
            <div className="stat-content">
              <div className="stat-number">{stats.pendingLeaves}</div>
              <div className="stat-label">Pending Leaves</div>
              <div className="stat-sub-badges">
                {stats.onLeaveToday > 0 && <span className="sub-badge info"><i className="fas fa-calendar-check"></i> {stats.onLeaveToday} on leave today</span>}
                {stats.pendingLeaves === 0 && <span className="sub-badge good">All clear</span>}
                {stats.pendingLeaves > 0 && <span className="sub-badge bad">Needs approval</span>}
              </div>
            </div>
            <i className="fas fa-arrow-right stat-arrow"></i>
          </div>


          {/* Active Premises */}
          <div className="stat-card clickable" onClick={() => navigate('/premises')}>
            <div className="stat-icon-wrap purple"><i className="fas fa-building"></i></div>
            <div className="stat-content">
              <div className="stat-number">{stats.totalPremises}</div>
              <div className="stat-label">Active Premises</div>
              <div className="stat-sub-badges">
                <span className="sub-badge neutral">
                  {isPremisesAdmin ? premisesName : 'All operational'}
                </span>
              </div>
            </div>
            <i className="fas fa-arrow-right stat-arrow"></i>
          </div>

        </div>

        {/* Monthly Hours banner */}
        {stats.monthlyHours > 0 && (
          <div className="hours-banner" onClick={() => navigate('/reports')}>
            <i className="fas fa-hourglass-half"></i>
            <span>This month your team has logged <strong>{stats.monthlyHours.toFixed(0)} hours</strong> of attendance</span>
            <i className="fas fa-arrow-right"></i>
          </div>
        )}

        {/* Main Content — Chart + Activity */}
        <div className="main-content-grid">
          <div className="chart-section">
            <div className="section-header">
              <h3>7-Day Attendance Trend</h3>
              <Link to="/reports" className="view-all-link">Full Reports <i className="fas fa-arrow-right"></i></Link>
            </div>
            <div className="trend-chart">
              {attendanceTrend.map((item, i) => (
                <div key={i} className="trend-bar-col" onClick={() => navigate(`/attendance?date=${item.date}`)}>
                  <div className="trend-pct-label">{item.percentage > 0 ? `${item.percentage}%` : ''}</div>
                  <div className="trend-bar-track">
                    <div
                      className="trend-bar-fill"
                      style={{
                        height: `${item.percentage}%`,
                        background: item.percentage >= 85 ? 'linear-gradient(to top,#27ae60,#2ecc71)' : item.percentage >= 60 ? 'linear-gradient(to top,#3498db,#5dade2)' : 'linear-gradient(to top,#e74c3c,#ec7063)'
                      }}
                    ></div>
                  </div>
                  <div className="trend-day-label">{item.day}</div>
                  {item.late > 0 && <div className="trend-late-badge">{item.late} late</div>}
                </div>
              ))}
            </div>
          </div>

          <div className="activity-section">
            <div className="section-header">
              <h3>Today's Summary</h3>
              <button className="refresh-btn" onClick={loadDashboardData}><i className="fas fa-sync-alt"></i></button>
            </div>
            <div className="activity-list">
              {/* Monthly Attendance Rate — pinned at top of Today's Summary */}
              <div
                className="activity-item monthly-att-row"
                onClick={() => navigate('/reports')}
                style={{ cursor: 'pointer', borderRadius: '8px', padding: '0.65rem 0.5rem' }}
              >
                <div className={`activity-icon ${stats.avgAttendance >= 85 ? 'success' : stats.avgAttendance >= 70 ? 'warning' : 'error'}`}>
                  <i className="fas fa-percentage"></i>
                </div>
                <div className="activity-content" style={{ flex: 1 }}>
                  <div className="activity-message" style={{ fontWeight: 600 }}>
                    Monthly Attendance Rate: {stats.avgAttendance.toFixed(1)}%
                  </div>
                  <div className="activity-time">
                    <span style={{ color: stats.avgAttendance >= 85 ? '#27ae60' : stats.avgAttendance >= 70 ? '#f39c12' : '#e74c3c', fontWeight: 600, fontSize: '0.78rem' }}>
                      {stats.avgAttendance >= 85 ? 'Excellent' : stats.avgAttendance >= 70 ? 'Good' : 'Needs attention'}
                    </span>
                    {' · This month'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '0.5rem' }}>
                  <div style={{ width: 48, height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(stats.avgAttendance, 100)}%`, height: '100%', background: stats.avgAttendance >= 85 ? '#27ae60' : stats.avgAttendance >= 70 ? '#f39c12' : '#e74c3c', borderRadius: 3 }} />
                  </div>
                </div>
              </div>
              {recentActivity.map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className={`activity-icon ${activity.color}`}><i className={activity.icon}></i></div>
                  <div className="activity-content">
                    <div className="activity-message">{activity.message}</div>
                    <div className="activity-time">{activity.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <div className="section-header"><h3>Quick Actions</h3></div>
          <div className="quick-actions-grid">
            {quickActions.map((action, i) => (
              <Link key={i} to={action.link} className={`quick-action-card ${action.color}`}>
                <div className="action-icon"><i className={action.icon}></i></div>
                <div className="action-content">
                  <div className="action-title">{action.title}</div>
                  <div className="action-description">{action.description}</div>
                </div>
                <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .admin-dashboard { min-height: 100vh; background: var(--light-color); }

        /* Welcome */
        .welcome-section {
          background: linear-gradient(135deg, #3498db, #2980b9);
          color: #fff; padding: var(--spacing-xl); border-radius: var(--radius-xl);
          margin-bottom: var(--spacing-xl); display: flex; justify-content: space-between;
          align-items: center; position: relative; overflow: hidden;
        }
        .welcome-section::before {
          content: ''; position: absolute; top: -50%; right: -20%;
          width: 100%; height: 200%;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="10" height="10" patternUnits="userSpaceOnUse"><circle cx="5" cy="5" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
          transform: rotate(15deg);
        }
        .welcome-content { position: relative; z-index: 1; }
        .welcome-content h1 { font-size: 2rem; margin-bottom: var(--spacing-sm); font-weight: 700; }
        .welcome-content p { font-size: 1.05rem; opacity: 0.9; margin: 0; }
        .premises-badge {
          margin-top: var(--spacing-md); display: inline-flex; align-items: center;
          gap: 0.5rem; background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.25);
          border-radius: 999px; padding: 0.4rem 0.9rem; font-size: 0.9rem;
        }
        .date-time { position: relative; z-index: 1; text-align: right; }
        .current-date { font-size: 1rem; margin-bottom: var(--spacing-xs); opacity: 0.9; }
        .current-time { font-size: 1.8rem; font-weight: bold; }

        /* Error */
        .error-message {
          background: rgba(231,76,60,0.1); color: var(--error);
          padding: var(--spacing-md); border-radius: var(--radius-md);
          margin-bottom: var(--spacing-xl); display: flex; align-items: center; gap: var(--spacing-sm);
        }

        /* Alerts */
        .alerts-section { margin-bottom: var(--spacing-xl); }
        .alerts-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--spacing-md); }
        .alerts-section-header h3 { margin: 0; font-size: 1.1rem; color: var(--text-dark); display: flex; align-items: center; gap: 0.5rem; }
        .alerts-count { background: var(--error); color: #fff; font-size: 0.72rem; font-weight: 700; padding: 0.18rem 0.5rem; border-radius: 999px; }
        .alerts-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: var(--spacing-md); }
        .alert-card { padding: var(--spacing-md); border-radius: var(--radius-lg); border-left: 4px solid; cursor: pointer; transition: transform 0.15s, box-shadow 0.15s; }
        .alert-card:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
        .alert-card.critical { background: rgba(231,76,60,0.08); border-left-color: #e74c3c; }
        .alert-card.critical:hover { background: rgba(231,76,60,0.13); }
        .alert-card.warning { background: rgba(243,156,18,0.08); border-left-color: #f39c12; }
        .alert-card.warning:hover { background: rgba(243,156,18,0.13); }
        .alert-card.info { background: rgba(52,152,219,0.08); border-left-color: #3498db; }
        .alert-card.info:hover { background: rgba(52,152,219,0.13); }
        .alert-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 0.35rem; }
        .alert-title-row { display: flex; align-items: center; gap: 0.4rem; flex: 1; }
        .alert-icon-sm { font-size: 0.88rem; }
        .alert-card.critical .alert-icon-sm { color: #e74c3c; }
        .alert-card.warning .alert-icon-sm { color: #f39c12; }
        .alert-card.info .alert-icon-sm { color: #3498db; }
        .alert-title { font-weight: 600; color: var(--text-dark); font-size: 0.92rem; }
        .alert-dismiss { background: none; border: none; color: var(--text-light); cursor: pointer; padding: 0.1rem 0.3rem; border-radius: 4px; flex-shrink: 0; }
        .alert-dismiss:hover { color: var(--text-dark); }
        .alert-message { font-size: 0.85rem; color: var(--text-dark); margin-bottom: 0.35rem; line-height: 1.4; }
        .alert-action { font-size: 0.8rem; font-weight: 600; }
        .alert-card.critical .alert-action { color: #e74c3c; }
        .alert-card.warning .alert-action { color: #f39c12; }
        .alert-card.info .alert-action { color: #3498db; }
        .alert-action.renew-action { font-size: 1.1rem; margin-top: 0.5rem; }

        /* Stats Grid */
        .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--spacing-lg); margin-bottom: var(--spacing-lg); }
        .stat-card {
          background: #fff; padding: var(--spacing-lg); border-radius: var(--radius-xl);
          box-shadow: var(--shadow-md); display: flex; align-items: flex-start;
          gap: var(--spacing-md); transition: transform 0.18s, box-shadow 0.18s;
          position: relative; overflow: hidden;
        }
        .stat-card.clickable { cursor: pointer; }
        .stat-card.clickable:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
        .stat-icon-wrap {
          width: 52px; height: 52px; border-radius: var(--radius-lg); flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #fff;
        }
        .stat-icon-wrap.primary { background: linear-gradient(135deg,#3498db,#2980b9); }
        .stat-icon-wrap.success { background: linear-gradient(135deg,#27ae60,#229954); }
        .stat-icon-wrap.warning { background: linear-gradient(135deg,#f39c12,#e67e22); }
        .stat-icon-wrap.info { background: linear-gradient(135deg,#17a2b8,#138496); }
        .stat-icon-wrap.purple { background: linear-gradient(135deg,#9b59b6,#8e44ad); }
        .stat-icon-wrap.danger { background: linear-gradient(135deg,#e74c3c,#c0392b); }
        .stat-content { flex: 1; min-width: 0; }
        .stat-number { font-size: 2.2rem; font-weight: 700; color: var(--text-dark); line-height: 1; }
        .stat-of { font-size: 1rem; font-weight: 400; color: var(--text-light); }
        .stat-label { font-size: 0.9rem; color: var(--text-light); margin: 0.3rem 0 0.5rem; }
        .stat-sub-badges { display: flex; flex-wrap: wrap; gap: 0.35rem; }
        .sub-badge { font-size: 0.75rem; font-weight: 600; padding: 0.18rem 0.55rem; border-radius: 999px; display: inline-flex; align-items: center; gap: 0.25rem; }
        .sub-badge.active { background: rgba(39,174,96,0.12); color: #27ae60; }
        .sub-badge.inactive { background: rgba(108,117,125,0.12); color: #6c757d; }
        .sub-badge.present { background: rgba(39,174,96,0.12); color: #27ae60; }
        .sub-badge.absent { background: rgba(231,76,60,0.12); color: #e74c3c; }
        .sub-badge.good { background: rgba(39,174,96,0.12); color: #27ae60; }
        .sub-badge.bad { background: rgba(231,76,60,0.12); color: #e74c3c; }
        .sub-badge.neutral { background: rgba(108,117,125,0.1); color: #6c757d; }
        .sub-badge.info { background: rgba(23,162,184,0.12); color: #17a2b8; }
        .stat-arrow { position: absolute; bottom: var(--spacing-md); right: var(--spacing-md); font-size: 0.85rem; color: var(--text-light); opacity: 0; transition: opacity 0.15s, transform 0.15s; }
        .stat-card.clickable:hover .stat-arrow { opacity: 1; transform: translateX(3px); }
        .license-bar { height: 5px; background: var(--light-color); border-radius: 3px; margin-top: 0.5rem; overflow: hidden; }
        .license-fill { height: 100%; border-radius: 3px; transition: width 0.4s; }

        /* Hours Banner */
        .hours-banner {
          background: linear-gradient(135deg, rgba(39,174,96,0.12), rgba(39,174,96,0.05));
          border: 1px solid rgba(39,174,96,0.25); border-radius: var(--radius-lg);
          padding: var(--spacing-md) var(--spacing-lg); display: flex; align-items: center;
          gap: var(--spacing-md); color: var(--text-dark); cursor: pointer;
          margin-bottom: var(--spacing-lg); transition: all 0.15s;
          font-size: 0.95rem;
        }
        .hours-banner:hover { background: rgba(39,174,96,0.15); transform: translateX(2px); }
        .hours-banner i:first-child { color: #27ae60; font-size: 1.2rem; }
        .hours-banner span { flex: 1; }
        .hours-banner i:last-child { color: var(--text-light); font-size: 0.85rem; }

        /* Chart + Activity */
        .main-content-grid { display: grid; grid-template-columns: 2fr 1fr; gap: var(--spacing-xl); margin-bottom: var(--spacing-xl); }
        .chart-section, .activity-section { background: #fff; border-radius: var(--radius-xl); box-shadow: var(--shadow-md); padding: var(--spacing-xl); }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--spacing-lg); }
        .section-header h3 { font-size: 1.15rem; font-weight: 600; color: var(--text-dark); margin: 0; }
        .view-all-link { color: #3498db; text-decoration: none; font-weight: 500; font-size: 0.88rem; display: flex; align-items: center; gap: 0.3rem; }
        .view-all-link:hover { text-decoration: underline; }
        .refresh-btn { background: none; border: none; color: var(--text-light); cursor: pointer; padding: 0.5rem; border-radius: var(--radius-sm); transition: all 0.15s; }
        .refresh-btn:hover { background: var(--light-color); color: #3498db; }

        /* Trend Chart */
        .trend-chart { display: flex; align-items: flex-end; gap: 6px; height: 200px; padding: 0.5rem 0; }
        .trend-bar-col { display: flex; flex-direction: column; align-items: center; flex: 1; cursor: pointer; }
        .trend-bar-col:hover .trend-bar-fill { filter: brightness(1.1); }
        .trend-pct-label { font-size: 0.7rem; color: var(--text-light); height: 1rem; margin-bottom: 3px; }
        .trend-bar-track { flex: 1; width: 100%; display: flex; align-items: flex-end; }
        .trend-bar-fill { width: 100%; border-radius: 4px 4px 0 0; min-height: 2px; transition: height 0.4s; }
        .trend-day-label { font-size: 0.78rem; color: var(--text-light); margin-top: 5px; }
        .trend-late-badge { font-size: 0.65rem; color: #e74c3c; margin-top: 2px; font-weight: 600; }

        /* Activity */
        .activity-list { max-height: 280px; overflow-y: auto; }
        .activity-item { display: flex; align-items: center; gap: var(--spacing-md); padding: var(--spacing-md) 0; border-bottom: 1px solid var(--light-color); }
        .activity-item:last-child { border-bottom: none; }
        .activity-icon { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.9rem; color: #fff; flex-shrink: 0; }
        .activity-icon.success { background: var(--success); }
        .activity-icon.warning { background: var(--warning); }
        .activity-icon.error { background: var(--error); }
        .activity-icon.info { background: var(--info); }
        .activity-message { font-size: 0.88rem; color: var(--text-dark); line-height: 1.4; }
        .activity-time { font-size: 0.75rem; color: var(--text-light); margin-top: 2px; }

        /* Quick Actions */
        .quick-actions-section { background: #fff; border-radius: var(--radius-xl); box-shadow: var(--shadow-md); padding: var(--spacing-xl); }
        .quick-actions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--spacing-md); }
        .quick-action-card { display: flex; align-items: center; gap: var(--spacing-md); padding: var(--spacing-md); border-radius: var(--radius-lg); text-decoration: none; color: var(--text-dark); transition: all 0.18s; border: 2px solid transparent; }
        .quick-action-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); text-decoration: none; color: var(--text-dark); }
        .quick-action-card.primary { background: rgba(52,152,219,0.08); }
        .quick-action-card.primary:hover { border-color: #3498db; }
        .quick-action-card.success { background: rgba(39,174,96,0.08); }
        .quick-action-card.success:hover { border-color: var(--success); }
        .quick-action-card.warning { background: rgba(243,156,18,0.08); }
        .quick-action-card.warning:hover { border-color: var(--warning); }
        .quick-action-card.info { background: rgba(23,162,184,0.08); }
        .quick-action-card.info:hover { border-color: var(--info); }
        .quick-action-card.purple { background: rgba(155,89,182,0.08); }
        .quick-action-card.purple:hover { border-color: #9b59b6; }
        .quick-action-card.secondary { background: rgba(108,117,125,0.08); }
        .quick-action-card.secondary:hover { border-color: #6c757d; }
        .action-icon { font-size: 1.7rem; opacity: 0.8; flex-shrink: 0; }
        .action-content { flex: 1; }
        .action-title { font-size: 0.95rem; font-weight: 600; margin-bottom: 0.2rem; }
        .action-description { font-size: 0.82rem; color: var(--text-light); line-height: 1.4; }
        .action-arrow { font-size: 0.95rem; opacity: 0.4; transition: all 0.15s; }
        .quick-action-card:hover .action-arrow { opacity: 1; transform: translateX(4px); }

        /* Responsive */
        @media (max-width: 1024px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .welcome-section { flex-direction: column; text-align: center; gap: var(--spacing-lg); }
          .welcome-content h1 { font-size: 1.6rem; }
          .date-time { text-align: center; }
          .current-time { font-size: 1.4rem; }
          .stats-grid { grid-template-columns: 1fr; }
          .main-content-grid { grid-template-columns: 1fr; }
          .quick-actions-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
